import { useState, useRef, useEffect } from "react";
import { Info } from "lucide-react";

export default function ChangeCard({
  metric,
  change,
  yoyChange,
  label,
  tooltip,
  prefix,
}: {
  metric: number | undefined;
  change: string | undefined;
  yoyChange: string | undefined;
  label: string;
  tooltip?: string;
  prefix?: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showTooltip) return;
    function handleClickOutside(event: MouseEvent) {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTooltip]);

  if (!metric) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      key={label.toLowerCase().replace(" ", "-")}
      className="relative rounded-lg border bg-card p-4 shadow-sm"
    >
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        {label}
        {tooltip && (
          <button
            type="button"
            className="ml-1"
            onClick={() => setShowTooltip((v) => !v)}
            aria-label="Show info"
          >
            <Info className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>
      {showTooltip && tooltip && (
        <div className="absolute top-2 right-2 z-10 w-64 p-2 rounded bg-background border shadow text-xs text-muted-foreground">
          {tooltip}
        </div>
      )}
      <div className="mt-1 text-2xl font-semibold">
        {prefix}
        {metric?.toLocaleString()}
      </div>
      {change && (
        <div
          className={`mt-2 text-xs ${
            parseInt(change) >= 0 ? "text-green-600" : "text-red-600"
          }`}
        >
          {parseInt(change) >= 0 ? "+" : ""}
          {change} vs prev period
        </div>
      )}

      {yoyChange && (
        <div
          className={`mt-2 text-xs ${
            parseInt(yoyChange) >= 0 ? "text-green-600" : "text-red-600"
          }`}
        >
          {parseInt(yoyChange) >= 0 ? "+" : ""}
          {yoyChange} vs this time last year
        </div>
      )}
    </div>
  );
}
